// Shared moments (issue #132): twice an hour something happens in every city, at the same instant for everyone
// watching it, with no server. When a city's own clock (the one under its name) reads :00, its landmark lights up, or
// there are small fireworks, a flight of lanterns or shooting stars by it; at :30 something of the city's crosses the
// sky: a flock of its birds, or a night flight. In the last LEAD_MIN minutes before one, the city's card counts down
// ("Fireworks in 3 min", site/js/app.js).
// Like the radio (site/js/audio/radio.js), nothing is sent: what happens, where and when is a pure function of the
// clock and the city, so every browser works out the same moment (momentOf) and draws it by the same clock (the live
// worker's where the page has it, else the device's). The drawing is site/js/ui/moments-draw.js, on a layer over the
// scene: a scene's render(t) stays its own 240 s loop, and PNG saves, the loop videos and the mini player never show a
// moment.
// "The hour" is the city's: New Delhi's clock is 30 minutes off most of the world's, so its hour turns at half past
// theirs. Whole hours of offset (and daylight saving, always a whole hour in these cities) change nothing here, only
// the minutes do (slipOf), so two browsers with different time zone data still agree.
// Tested in tests/moments.test.mjs.
import { W, clamp, hash } from '../engine/core.js';
import { frameOf } from '../engine/display.js';

export const HALF_MS = 30 * 60_000;                 // a slot: from one moment to the next
export const LEAD_MIN = 5, LEAD_MS = LEAD_MIN * 60_000;   // the countdown shows in a moment's last LEAD_MIN minutes
const LONGEST_MS = 60_000;                          // no moment is on for longer than this after its instant

// The kinds. slot: when it comes. life: seconds an hour kind shows from its instant. speed: scene pixels a second of
// something that crosses. name: what the countdown calls it (a glow and a flock are named by the city). sky: drawn in
// the city's `sky` boxes. flashes: sudden light, so with reduced motion the city's glow comes in its place. dry: needs
// a dry sky, so under a picked drizzle, rain, storm or snow the glow comes too.
export const KINDS = {
  glow:      { slot: 'hour', life: 26 },
  fireworks: { slot: 'hour', life: 34, name: 'Fireworks', sky: true, flashes: true },
  lanterns:  { slot: 'hour', life: 46, name: 'Lanterns', sky: true, dry: true },
  stars:     { slot: 'hour', life: 30, name: 'Shooting stars', sky: true, flashes: true, dry: true },
  flock:     { slot: 'half', speed: 15 },
  flight:    { slot: 'half', speed: 13, name: 'A night flight', span: 13 },
};
const WET = ['drizzle', 'rain', 'thunderstorm', 'snow'];

// How a flock flies (n birds over `span` pixels, nose to tail) and the birds a city can name: [formation, tint].
export const FORMS = { loose: { n: 7, span: 52 }, vee: { n: 9, span: 30 }, cloud: { n: 22, span: 66 } };
export const BIRDS = {
  gulls: ['loose', 'pale'], pigeons: ['loose', 'grey'], crows: ['loose', 'dusk'], jackdaws: ['loose', 'dusk'],
  magpies: ['loose', 'grey'], parakeets: ['loose', 'green'], frigatebirds: ['loose', 'dusk'],
  'black kites': ['loose', 'dusk'], 'flying foxes': ['loose', 'dusk'],
  geese: ['vee', 'grey'], egrets: ['vee', 'pale'], storks: ['vee', 'pale'], pelicans: ['vee', 'grey'],
  starlings: ['cloud', 'dusk'], swifts: ['cloud', 'dusk'], swallows: ['cloud', 'grey'],
};

// Every scene's moments, by scene id (a city's other look has its own: another view, another sky).
//   hour  the kind at :00. `lit` names its glow ("… in 3 min"), which is also what comes in place of a kind that
//         flashes or needs a dry sky (KINDS).
//   glow  [cx, cy, rx, ry]: the light on the landmark, an ellipse in scene pixels.
//   half  what crosses at :30: 'flight', or the city's birds (BIRDS).
//   lane  [y, x0, x1, …]: the row it crosses on, then the columns where something nearer hides it (a building, a tree,
//         a window bar, the landmark itself), in pairs, edges included. It flies behind those, never over them.
//   sky   boxes [x0, y0, x1, y1] (edges included) of open air by the landmark, where fireworks, lanterns and shooting
//         stars are drawn and clipped: they rise from behind whatever a box ends on. Needed by those kinds only. The
//         first box is the one a phone held upright shows.
//   more  name (the countdown's word for the hour kind here), sparkle ([x, y, w, h]: the landmark's box, where lights
//         twinkle while it glows, tighter towards its top), pane (a box the glow is clipped to: Paris and Tokyo's room
//         are seen through a window), tint (the birds' colour under this sky).
// All of it was read off the scenes as drawn (rows 31 and up are on every landscape screen, rows 55 and up clear of a
// phone's city card), so a scene that moves its landmark needs its entry looked at again.
const city = (hour, lit, glow, half, lane, more = {}) => ({ hour, lit, glow, half, lane, ...more });
export const MOMENTS = {
  paris: city('glow', 'The tower sparkles', [214, 114, 20, 25], 'pigeons', [68, 0, 147, 237, 241, 332, 479],
    { sparkle: [205, 91, 19, 47], pane: [148, 89, 236, 138], sky: [[148, 42, 236, 85], [242, 42, 331, 85]] }),
  tokyo: city('fireworks', 'Tokyo Tower lights up', [246, 84, 24, 52], 'flight', [56, 0, 108, 148, 152, 245, 247],
    { sky: [[168, 40, 232, 102], [260, 40, 398, 100]] }),
  // the same night from a room: everything is behind its window's glass, whose head slopes (row 53 on the left, 75 on
  // the right), so the boxes and the lane sit under it and the flight passes behind the stiles and the two mullions
  'tokyo-room': city('fireworks', 'Tokyo Tower lights up', [232, 117, 18, 38], 'flight', [84, 0, 69, 158, 164, 230, 234, 300, 305, 345, 479],
    { pane: [165, 69, 266, 178], sky: [[167, 66, 214, 102], [250, 73, 298, 108], [72, 61, 156, 100]] }),
  'new-york': city('fireworks', 'The Empire State lights up', [189, 72, 22, 54], 'flight', [40, 37, 39, 188, 190, 371, 479],
    { sky: [[202, 30, 366, 76], [46, 30, 176, 90]] }),
  london: city('glow', 'Big Ben lights up', [206, 84, 24, 54], 'flight', [55, 20, 136, 200, 212],
    { sky: [[218, 36, 268, 100], [136, 36, 194, 100]] }),
  rio: city('glow', 'Christ the Redeemer lights up', [158, 38, 24, 22], 'frigatebirds', [60, 463, 479]),
  istanbul: city('glow', 'The mosques light up', [180, 108, 60, 34], 'gulls', [67, 8, 22, 62, 74, 426, 440]),
  'hong-kong': city('fireworks', 'The skyline lights up', [245, 84, 44, 50], 'black kites', [66, 0, 77, 90, 106, 217, 233, 258, 278, 322, 330],
    { sky: [[158, 44, 214, 98], [108, 44, 156, 98], [280, 40, 318, 78], [336, 40, 478, 80]] }),
  sydney: city('fireworks', 'The Opera House lights up', [228, 138, 78, 36], 'flying foxes', [60, 0, 222],
    { sky: [[226, 48, 304, 100], [312, 30, 478, 84]] }),
  'san-francisco': city('stars', 'The bridge lights up', [290, 124, 96, 28], 'pelicans', [80, 32, 36],
    { sky: [[4, 6, 476, 92]] }),
  hamburg: city('fireworks', 'The Elbphilharmonie lights up', [223, 100, 66, 48], 'gulls', [46, 394, 400, 444, 464],
    { sky: [[286, 40, 360, 92], [20, 40, 160, 100]] }),
  amsterdam: city('glow', 'The tower lights up', [225, 62, 24, 58], 'geese', [60, 215, 235, 306, 446]),
  dubai: city('glow', 'The Burj lights up', [240, 74, 20, 64], 'flight', [58, 0, 92, 235, 246, 362, 468]),
  madrid: city('glow', 'The dome lights up', [200, 82, 28, 42], 'swifts', [56, 190, 210, 424, 479]),
  rome: city('glow', 'The fountain lights up', [240, 96, 56, 56], 'starlings', [15, 0, 24, 440, 479]),
  'rome-colosseum': city('stars', 'The Colosseum lights up', [227, 158, 74, 32], 'swifts', [50, 0, 116, 362, 479],
    { sky: [[118, 4, 360, 76]] }),
  prague: city('fireworks', 'The castle lights up', [235, 70, 44, 50], 'jackdaws', [60, 0, 126, 200, 245],
    { sky: [[248, 46, 388, 92], [130, 26, 198, 92]] }),
  munich: city('glow', 'The Rathaus lights up', [186, 70, 28, 56], 'crows', [24, 181, 191, 396, 479]),
  vancouver: city('glow', 'The sails light up', [311, 134, 46, 22], 'geese', [70, 48, 52, 398, 479]),
  seoul: city('glow', 'Namsan Tower lights up', [172, 72, 22, 50], 'magpies', [50, 0, 10, 170, 174, 398, 479]),
  bangkok: city('lanterns', 'Wat Arun lights up', [215, 96, 36, 62], 'egrets', [46, 0, 166, 209, 219],
    { name: 'Sky lanterns', sky: [[236, 16, 478, 106]] }),
  'cape-town': city('glow', 'Table Mountain lights up', [235, 76, 100, 26], 'flight', [44]),
  singapore: city('fireworks', 'Marina Bay lights up', [272, 104, 66, 52], 'flight', [40, 0, 72],
    { sky: [[214, 28, 330, 52], [84, 30, 208, 108], [336, 28, 470, 54]] }),
  'mexico-city': city('lanterns', 'Bellas Artes lights up', [212, 128, 72, 40], 'flight', [56, 318, 340],
    { name: 'Paper lanterns', sky: [[84, 36, 314, 96]] }),
  'new-delhi': city('fireworks', 'India Gate lights up', [242, 112, 44, 52], 'black kites', [50, 360, 479],
    { sky: [[146, 24, 200, 96], [284, 24, 356, 100]] }),
  cairo: city('glow', 'The pyramids light up', [187, 98, 44, 42], 'pigeons', [50, 0, 9, 366, 388]),
  lisbon: city('glow', 'The dome lights up', [232, 86, 38, 50], 'flight', [56, 214, 250, 466, 479]),
  osaka: city('glow', 'Dotonbori lights up', [236, 76, 30, 54], 'flight', [12]),
  berlin: city('glow', 'The Gate lights up', [240, 112, 86, 44], 'crows', [42, 0, 112, 392, 479]),
  athens: city('glow', 'The Parthenon lights up', [243, 62, 74, 28], 'swallows', [28, 370, 479]),
  copenhagen: city('glow', 'Nyhavn lights up', [170, 94, 40, 36], 'gulls', [36], { tint: 'dark' }),
  seattle: city('glow', 'The Needle lights up', [168, 58, 28, 40], 'crows', [62, 156, 180]),
  florence: city('glow', 'The Duomo lights up', [268, 76, 42, 48], 'swifts', [36, 109, 115, 265, 275], { tint: 'dark' }),
  dublin: city('glow', 'The bridge lights up', [181, 134, 150, 24], 'gulls', [48, 396, 479]),
  miami: city('fireworks', 'The marquee lights up', [124, 92, 34, 50], 'pelicans', [56, 116, 130, 340, 430],
    { sky: [[132, 30, 325, 66], [20, 30, 104, 92]] }),
  budapest: city('fireworks', 'Parliament lights up', [190, 118, 60, 44], 'gulls', [50],
    { sky: [[78, 30, 182, 92], [196, 30, 420, 92]] }),
  kyoto: city('glow', 'The pagoda lights up', [241, 78, 28, 52], 'egrets', [62, 26, 144, 222, 260, 378, 479]),
  venice: city('glow', 'The Rialto lights up', [250, 108, 100, 28], 'gulls', [52, 0, 38, 105, 115, 416, 479]),
  marrakech: city('stars', 'The Koutoubia lights up', [233, 72, 24, 56], 'storks', [56, 222, 246],
    { sky: [[250, 14, 470, 86], [20, 14, 216, 90]] }),
  'buenos-aires': city('glow', 'The Obelisco lights up', [240, 78, 20, 56], 'flight', [56, 0, 184, 233, 247, 452, 479]),
  chicago: city('fireworks', 'The tower lights up', [203, 84, 26, 56], 'flight', [56, 192, 212],
    { sky: [[226, 34, 356, 86], [60, 34, 180, 92]] }),
  barcelona: city('glow', 'The Sagrada Família lights up', [250, 76, 60, 58], 'parakeets', [30, 0, 45, 232, 248, 296, 305, 378, 479]),
  stockholm: city('glow', 'City Hall lights up', [322, 100, 44, 62], 'gulls', [36, 316, 330]),
  taipei: city('fireworks', 'Taipei 101 lights up', [196, 86, 18, 74], 'flight', [64, 0, 72, 182, 210],
    { sparkle: [182, 14, 30, 145], sky: [[104, 40, 176, 112], [214, 34, 300, 92], [318, 50, 478, 112]] }),
  chongqing: city('glow', 'Hongyadong lights up', [291, 126, 64, 30], 'flight', [32]),
  melbourne: city('glow', 'Flinders Street Station lights up', [250, 128, 84, 50], 'gulls', [50, 390, 448]),
  'new-orleans': city('glow', 'St Louis Cathedral lights up', [240, 86, 38, 58], 'flight', [58, 0, 3, 236, 244, 453, 479]),
  toronto: city('glow', 'The CN Tower lights up', [178, 77, 16, 58], 'geese', [60, 0, 112, 177, 179, 410, 479],
    { sparkle: [172, 20, 13, 114] }),
  boston: city('glow', 'The Prudential lights up', [398, 112, 16, 58], 'geese', [56, 0, 127, 396, 479],
    { sparkle: [388, 52, 20, 116] }),
  'boston-public-garden': city('glow', 'The footbridge lights up', [309, 146, 76, 21], 'geese', [60, 26, 27, 42, 80, 404, 479]),
  'boston-acorn-street': city('glow', 'Acorn Street lights up', [263, 128, 24, 44], 'pigeons', [40, 0, 235, 342, 371, 418, 479]),
  'boston-old-state-house': city('glow', 'The Old State House lights up', [222, 112, 44, 84], 'gulls', [58, 0, 102, 238, 262],
    { sparkle: [236, 28, 28, 52] }),
  'boston-faneuil-hall': city('glow', 'Faneuil Hall lights up', [204, 130, 70, 58], 'pigeons', [60],
    { sparkle: [130, 70, 147, 123] }),
  'boston-fenway': city('fireworks', 'The Monster lights up', [189, 117, 97, 18], 'gulls', [40, 135, 143, 263, 271],
    { sky: [[146, 32, 258, 56], [290, 30, 476, 54]] }),
  'los-angeles': city('fireworks', 'The Hollywood Sign lights up', [254, 44, 56, 16], 'flight', [30, 160, 196, 318, 350],
    { sky: [[352, 30, 432, 53], [56, 31, 156, 60]], sparkle: [205, 38, 99, 13] }),
  bali: city('stars', 'Tanah Lot lights up', [207, 100, 122, 64], 'flying foxes', [56, 454, 460],
    { sparkle: [86, 35, 243, 127], sky: [[232, 24, 290, 46], [316, 22, 396, 56]] }),
  'las-vegas': city('glow', 'The Eiffel Tower sparkles', [284, 96, 24, 66], 'flight', [60, 3, 38, 279, 289, 326, 338, 448, 479],
    { sparkle: [262, 34, 45, 124] }),
  // the frigatebirds cross behind the near towers and the palm's crown; the fireworks rise from behind Diamond Head
  honolulu: city('fireworks', 'Diamond Head lights up', [265, 120, 85, 28], 'frigatebirds', [60, 0, 31, 51, 80, 356, 479],
    { sky: [[284, 36, 354, 88], [140, 40, 242, 100]] }),
  // the pigeons cross behind the Capitolio's cupola and the corner building's cornice
  havana: city('glow', 'El Capitolio lights up', [300, 68, 32, 50], 'pigeons', [60, 277, 323, 398, 479],
    { sparkle: [269, 17, 63, 101] }),
  // the flying foxes cross behind the house's aerial, the Taj's dome and the harsingar's crown
  agra: city('glow', 'The Taj Mahal sparkles', [240, 96, 64, 58], 'flying foxes', [58, 54, 66, 227, 253, 421, 479],
    { sparkle: [152, 35, 177, 120] }),
  // the gulls cross behind the Clérigos tower, the Sé's two towers and Serra do Pilar's drum
  porto: city('glow', 'The Dom Luís I bridge lights up', [297, 118, 112, 34], 'gulls', [60, 125, 137, 150, 157, 174, 181, 458, 479],
    { sparkle: [183, 82, 228, 70] }),
  // the gulls cross over the street, behind the bookshop's gable at its peak
  reykjavik: city('glow', 'Hallgrímskirkja lights up', [240, 90, 26, 68], 'gulls', [50, 410, 440]),
  // the swifts cross behind Menara KL's mast, the twin towers' crowns, the glass tower's cut top and the rain tree
  'kuala-lumpur': city('glow', 'The Petronas Towers light up', [254, 80, 34, 70], 'swifts', [56, 159, 161, 225, 241, 267, 283, 351, 367, 397, 479],
    { sparkle: [222, 8, 65, 142] }),
  warsaw: city('glow', 'Sigismund\'s Column lights up', [198, 117, 18, 82], 'jackdaws', [60, 4, 51, 192, 205, 309, 317],
    { sparkle: [187, 35, 23, 165] }),
  manila: city('fireworks', 'Manila Bay lights up', [230, 120, 110, 18], 'egrets', [52, 0, 106, 370, 479],
    { sky: [[150, 56, 330, 100]] }),
  // the geese cross behind the Wells Fargo Center, the sign and Big Pink
  portland: city('glow', 'The Portland Oregon sign lights up', [280, 80, 50, 52], 'geese', [60, 197, 210, 236, 329, 345, 366],
    { sparkle: [232, 30, 97, 99] }),
  // the gulls cross behind the Carmelite dome's lantern and the Pro-Cathedral's spire; festa fireworks over the roofs
  valletta: city('fireworks', 'The Carmelite dome lights up', [204, 64, 26, 30], 'gulls', [58, 197, 211, 264, 272],
    { sparkle: [181, 34, 47, 59], sky: [[282, 40, 344, 96], [86, 36, 166, 88]] }),
  // the crows cross over the range, behind the TV tower's mast, the corner domes' crosses, the central onion and the belfry's drum
  almaty: city('glow', 'The Ascension Cathedral lights up', [300, 104, 80, 74], 'crows', [58, 127, 129, 244, 248, 271, 281, 305, 309, 332, 336],
    { sparkle: [222, 28, 158, 150] }),
  helsinki: city('glow', 'The Cathedral lights up', [140, 100, 52, 52], 'gulls', [64, 127, 155, 373, 385],
    { sparkle: [112, 48, 57, 92] }),
  'ho-chi-minh-city': city('glow', 'Bitexco lights up', [153, 82, 20, 58], 'flight', [60, 0, 128, 139, 168],
    { sparkle: [138, 24, 32, 116] }),
  auckland: city('glow', 'The Sky Tower lights up', [296, 76, 16, 70], 'gulls', [50, 294, 298, 418, 479],
    { sparkle: [284, 8, 25, 133] }),
  cartagena: city('fireworks', 'The Torre del Reloj lights up', [196, 62, 34, 58], 'frigatebirds', [60, 183, 209, 398, 479],
    { sparkle: [178, 37, 37, 59], sky: [[114, 30, 162, 78], [232, 40, 306, 96]] }),
  nairobi: city('glow', 'Britam Tower lights up', [150, 84, 16, 50], 'egrets', [64, 141, 158, 235, 237],
    { sparkle: [141, 31, 18, 102] }),
  // the pigeons cross behind the Tower of Hope and the Esplanade Riel's pylon with its fan of stays
  winnipeg: city('glow', 'The Tower of Hope lights up', [125, 61, 16, 30], 'pigeons', [64, 119, 132, 328, 364],
    { sparkle: [117, 32, 17, 58] }),
};

/* ---------- when ---------- */
const minuteOf = new Map();                         // time zone -> its formatter
const slips = new Map();                            // time zone -> { hour, slip }: asked every frame, worked out once an hour
/**
 * How many minutes the clock of `timeZone` is ahead of UTC's minute hand at `ms` (epoch): 0 nearly everywhere, 30 in
 * India, 45 in Nepal. Whole hours don't count, so daylight saving and a browser with older time zone data change nothing.
 */
export function slipOf(timeZone, ms) {
  const hour = Math.floor(ms / 3_600_000), kept = slips.get(timeZone);
  if (kept?.hour === hour) return kept.slip;
  let f = minuteOf.get(timeZone);
  if (!f) minuteOf.set(timeZone, f = new Intl.DateTimeFormat('en-GB', { timeZone, minute: 'numeric' }));
  const slip = (((Number(f.format(ms)) - Math.floor(ms / 60_000)) % 60) + 60) % 60;
  slips.set(timeZone, { hour, slip });
  return slip;
}

/**
 * The slot `ms` (epoch) is in on a clock `slip` minutes ahead of UTC's minute hand: { n, at }. n counts the half
 * hours since 1970 on that clock, the same number in every browser (even: the slot began on the hour); at: when it
 * began (epoch ms), which is its moment's instant.
 */
export function slotAt(ms, slip = 0) {
  const n = Math.floor((ms + slip * 60_000) / HALF_MS);
  return { n, at: n * HALF_MS - slip * 60_000 };
}

/** A whole number from a scene's id and a slot, the same everywhere: what a moment draws its variety from. */
export function seedOf(id, n) {
  let h = 2166136261;
  for (const ch of id) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  h = Math.imul(h ^ n, 2654435761); h ^= h >>> 15;
  h = Math.imul(h, 2246822519); h ^= h >>> 13;
  h = Math.imul(h, 3266489917);
  return (h ^ (h >>> 16)) >>> 0;
}

/* ---------- what ---------- */
/** Where a scene's views meet: the middle of what every screen shows of it (its landmark and billboard). */
export const anchorOf = scene => { const [a, b] = frameOf(scene); return clamp(Math.round((a + b) / 2), 60, W - 60); };

/**
 * The kind slot `n` brings to a city (`entry`, of MOMENTS). still: prefers-reduced-motion; weather: the visitor's pick
 * (site/js/engine/weather.js). The city's glow stands in for a kind that flashes when motion is reduced, and for one
 * that needs a dry sky under a wet pick. `kind` asks for another kind than the city's own (contact sheets, tests).
 */
export function kindOf(entry, n, { still = false, weather = 'city', kind = null } = {}) {
  const hour = n % 2 === 0;
  const k = kind ?? (hour ? entry.hour : entry.half === 'flight' ? 'flight' : 'flock'), K = KINDS[k];
  return K.slot === 'hour' && ((still && K.flashes) || (K.dry && WET.includes(weather))) ? 'glow' : k;
}

const kept = new Map();                             // the last few moments worked out: one is asked for every frame
/**
 * The moment of slot `n` over scene `id` (`scene`: its module, for what it exports), on a clock `slip` minutes ahead
 * (slipOf), or null for a scene without an entry. { id, n, slot, kind, name, at, from, to, seed, still, accent, … }:
 * `at` is its instant (epoch ms: the city's clock reads :00 or :30), `from` and `to` when it is drawn. An hour kind
 * starts at its instant: + glow [cx, cy, rx, ry], sky, pane, sparkle ([x, y, w, h] or null), billboard (the scene's,
 * which a glow leaves alone). Something that
 * crosses is over the middle of the scene's frame at its instant (`anchor`; beside whatever hides that column), so
 * every screen has it in view then, a phone's narrow one too, and starts and ends where it leaves the scene: + y, gaps
 * [[x0, x1]], dir (1: left to right), anchor, speed, span, form, tint.
 */
export function momentOf(id, scene, n, slip = 0, opts = {}) {
  const entry = MOMENTS[id];
  if (!entry) return null;
  const { still = false, weather = 'city', kind: asked = null } = opts;
  const key = `${id}|${n}|${slip}|${still}|${weather}|${asked}`;
  if (kept.has(key)) return kept.get(key);
  const kind = kindOf(entry, n, opts), K = KINDS[kind], at = n * HALF_MS - slip * 60_000, seed = seedOf(id, n);
  const m = { id, n, slot: K.slot, kind, at, seed, still, accent: scene?.accent ?? '#ffd27a' };
  if (K.slot === 'hour') {
    Object.assign(m, {
      name: kind === 'glow' ? entry.lit : entry.name ?? K.name, from: at, to: at + K.life * 1000,
      glow: entry.glow, sky: entry.sky ?? [], pane: entry.pane ?? null, sparkle: entry.sparkle ?? null,
      billboard: scene?.billboard ?? null,
    });
  } else {
    const [y, ...xs] = entry.lane, gaps = [];
    for (let i = 0; i + 1 < xs.length; i += 2) gaps.push([xs[i], xs[i + 1]]);
    const birds = kind === 'flock' ? BIRDS[entry.half] ?? BIRDS.gulls : null, form = birds?.[0] ?? null;
    const span = form ? FORMS[form].span : K.span, dir = hash(seed, 1) < 0.5 ? 1 : -1, edge = 4;
    let anchor = anchorOf(scene);                   // where it is at its instant: in the open, not behind something
    const behind = gaps.find(([a, b]) => anchor >= a && anchor <= b);
    if (behind) anchor = behind[0] - 8 >= 60 && anchor - behind[0] <= behind[1] - anchor ? behind[0] - 8 : Math.min(W - 60, behind[1] + 8);
    const before = (dir > 0 ? anchor : W - anchor) + edge, after = (dir > 0 ? W - anchor : anchor) + span + edge;
    Object.assign(m, {
      name: kind === 'flock' && BIRDS[entry.half] ? `A flock of ${entry.half}` : K.name ?? 'A flock of birds',
      from: at - Math.round(before / K.speed * 1000), to: at + Math.round(after / K.speed * 1000),
      y, gaps, dir, anchor, speed: K.speed, span, form, tint: entry.tint ?? birds?.[1] ?? null,
    });
  }
  if (kept.size > 12) kept.clear();
  kept.set(key, m);
  return m;
}

/**
 * The moment that `ms` (epoch) is near over scene `id`: the one on now, or the one within its countdown; null the
 * rest of the time, which is most of it (asked every frame: nothing is made then).
 */
export function momentNear(id, scene, ms, slip = 0, opts) {
  if (!MOMENTS[id]) return null;
  const { n, at } = slotAt(ms, slip), since = ms - at;
  if (since < LONGEST_MS) {
    const m = momentOf(id, scene, n, slip, opts);
    if (ms < m.to) return m;
  }
  if (since >= HALF_MS - LEAD_MS) return momentOf(id, scene, n + 1, slip, opts);
  return null;
}

/** Whether moment `m` is being drawn at `ms`. */
export const playing = (m, ms) => !!m && ms >= m.from && ms < m.to;

/** Whole minutes until moment `m` at `ms`, rounded up: 5 down to 1 through its countdown. */
export const minutesTo = (m, ms) => Math.max(1, Math.ceil((m.at - ms) / 60_000));

/**
 * The city card's line for moment `m` at `ms`: "Fireworks in 3 min" from LEAD_MIN minutes before it until it starts to
 * show, '' otherwise. It changes once a minute.
 */
export function countdownOf(m, ms) {
  return m && ms >= m.at - LEAD_MS && ms < Math.min(m.from, m.at) ? `${m.name} in ${minutesTo(m, ms)} min` : '';
}
